import { type ReactElement, useMemo, useState } from 'react'
import { SelectableOption } from 'app/components/pickers'
import { isEmpty, searchIncludes } from '@terros/common'
import { Combobox, Group, Input, InputBase, ScrollArea, useCombobox } from '@mantine/core'
import { useDebounce } from '@hooks'
import { availableMaterialSymbols, isMaterialSymbol, Size, type MaterialSymbolType, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from './MaterialSymbolIcon'

type Props = {
  value: string | undefined
  onChange: (value: string) => void
  withinPortal?: boolean
}

export const MaterialSymbolSelector = ({ value, onChange, withinPortal }: Props): ReactElement => {
  const { t } = useTranslation()
  const combobox = useCombobox({
    onDropdownOpen: () => combobox.focusSearchInput(),
    onDropdownClose: () => combobox.resetSelectedOption(),
  })
  const [search, setSearch] = useState('')
  const filter = useDebounce(search.toLowerCase(), 500)

  const displayIcons = useMemo(() => {
    if (isEmpty(filter)) return []
    return availableMaterialSymbols.filter((icon) => searchIncludes([icon, normalizeIcon(icon)], filter)).slice(0, 100)
  }, [filter])

  const options = displayIcons
    .slice(0, 50)
    .map((item) => <OptionDisplay key={item} icon={item} selected={item === value} />)

  return (
    <Input.Wrapper>
      <Input.Description>
        You can visit{' '}
        <a
          href='https://fonts.google.com/icons?icon.style=Rounded&icon.set=Material+Symbols'
          target='_blank'
          rel='noopener noreferrer'
          className='text-blue-500'
        >
          https://fonts.google.com/icons
        </a>{' '}
        to search for icons.
      </Input.Description>
      <Combobox
        store={combobox}
        withinPortal={withinPortal}
        onOptionSubmit={(val) => {
          onChange(val)
          combobox.closeDropdown()
        }}
        width={300}
        position='bottom-start'
      >
        <Combobox.Target>
          <InputBase
            pointer
            component='button'
            rightSection={<Combobox.Chevron />}
            rightSectionPointerEvents='none'
            type='button'
            onClick={() => combobox.toggleDropdown()}
          >
            {value && isMaterialSymbol(value) ? (
              <ItemDisplay icon={value} />
            ) : (
              <Input.Placeholder>{t('Pick value')}</Input.Placeholder>
            )}
          </InputBase>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Search
            placeholder={t('Search icons...')}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <Combobox.Options>
            <ScrollArea.Autosize mah={200} type='scroll'>
              {isEmpty(filter) && isMaterialSymbol(value) && <OptionDisplay icon={value} selected />}
              {options}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Input.Wrapper>
  )
}

const OptionDisplay = ({ icon, selected }: { icon: MaterialSymbolType; selected: boolean }): ReactElement => {
  return (
    <SelectableOption value={icon} selected={selected}>
      <ItemDisplay icon={icon} />
    </SelectableOption>
  )
}

const ItemDisplay = ({ icon }: { icon: MaterialSymbolType }): ReactElement => {
  return (
    <Group gap={Size.space.md} wrap='nowrap'>
      <MaterialSymbolIcon icon={icon} />
      {normalizeIcon(icon)}
    </Group>
  )
}

const normalizeIcon = (icon: MaterialSymbolType): string => icon.split('_').join(' ')
